import { useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, LogOut, Command, Gem, CheckCircle2, Loader2, AlertCircle, Bell, BellRing, LayoutGrid } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { NAV } from "@/components/nav";
import { useAuth } from "@/context/AuthContext";
import { useFinancialState } from "@/context/StateContext";
import { billReminders, fmtMoney } from "@/lib/finance";

const SaveIndicator = () => {
  const { saveStatus } = useFinancialState();
  const map = {
    saving: { icon: Loader2, text: "Saving…", cls: "text-[var(--text-muted)]", spin: true },
    saved: { icon: CheckCircle2, text: "Saved", cls: "text-[var(--green)]" },
    error: { icon: AlertCircle, text: "Save failed", cls: "text-[var(--danger)]" },
    idle: { icon: CheckCircle2, text: "All changes saved", cls: "text-[var(--text-faint)]" },
  };
  const s = map[saveStatus] || map.idle;
  const Icon = s.icon;
  return (
    <div className={cn("hidden sm:flex items-center gap-1.5 text-xs", s.cls)} data-testid="save-indicator">
      <Icon className={cn("h-3.5 w-3.5", s.spin && "animate-spin")} />
      <span>{s.text}</span>
    </div>
  );
};

const NotificationBell = () => {
  const { state } = useFinancialState();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { overdue, dueSoon } = billReminders(state?.bills || []);
  const items = [
    ...overdue.map((b) => ({ ...b, kind: "overdue" })),
    ...dueSoon.map((b) => ({ ...b, kind: "soon" })),
  ];
  const count = items.length;
  const go = () => { setOpen(false); navigate("/wealth/budget"); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9 text-[var(--text-muted)] hover:text-[var(--text)]" data-testid="notification-bell" aria-label="Notifications">
          {count > 0 ? <BellRing className="h-[18px] w-[18px]" /> : <Bell className="h-[18px] w-[18px]" />}
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-[var(--gold)] text-[10px] font-semibold text-white" data-testid="notification-count">{count}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-[var(--surface-2)] border border-[color:var(--border-subtle)]" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-[color:var(--border-subtle)]">
          <div className="font-serif-display text-[var(--text)]">Notifications</div>
          <div className="text-[11px] text-[var(--text-faint)]">Upcoming & overdue bills</div>
        </div>
        <div className="max-h-80 overflow-auto">
          {count === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[var(--text-muted)]">
              <CheckCircle2 className="h-6 w-6 text-[var(--green)] mx-auto mb-2" />
              You're all caught up — no bills due soon.
            </div>
          ) : (
            items.map((b) => (
              <button key={b.id} onClick={go} data-testid={`notification-item-${b.id}`}
                className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[color:rgba(31,115,85,0.06)] transition-colors border-b border-[color:var(--border-subtle)] last:border-0">
                <span className={cn("grid place-items-center h-8 w-8 rounded-full shrink-0",
                  b.kind === "overdue" ? "bg-[color:rgba(180,35,24,0.12)] text-[var(--danger)]" : "bg-[color:rgba(198,139,60,0.16)] text-[var(--gold)]")}>
                  <BellRing className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[var(--text)] truncate">{b.name}</div>
                  <div className="text-[11px] text-[var(--text-faint)]">{b.kind === "overdue" ? "Overdue" : "Due soon"} · day {b.dueDay}</div>
                </div>
                <span className="tabular-nums text-sm text-[var(--text)]">{fmtMoney(b.amount)}</span>
              </button>
            ))
          )}
        </div>
        {count > 0 && (
          <button onClick={go} className="w-full text-center py-2.5 text-sm text-[var(--green)] hover:bg-[color:rgba(31,115,85,0.06)] transition-colors border-t border-[color:var(--border-subtle)]" data-testid="notification-view-all">
            Manage bills
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};

const NavList = ({ onNavigate }) => {
  const { pathname } = useLocation();
  return (
    <nav className="px-3 py-2 space-y-1">
      {NAV.map((item) => {
        const active = item.to === "/wealth" ? pathname === "/wealth" : pathname.startsWith(item.to);
        const Icon = item.icon;
        return (
          <NavLink
            key={item.key}
            to={item.to}
            end={item.to === "/wealth"}
            onClick={onNavigate}
            data-testid={`sidebar-nav-item-${item.key}`}
            className={cn(
              "group relative flex items-center gap-3 rounded-[12px] px-3 py-2 text-sm transition-colors",
              active
                ? "bg-[color:rgba(31,115,85,0.12)] text-[var(--text)] font-medium"
                : "text-[var(--text-muted)] hover:bg-[color:rgba(31,115,85,0.07)] hover:text-[var(--text)]"
            )}
          >
            {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-[var(--green)]" />}
            <Icon className={cn("h-[18px] w-[18px]", active ? "text-[var(--green)]" : "text-[color:rgba(23,35,31,0.6)] group-hover:text-[var(--green)]")} />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
};

const Brand = () => (
  <div className="flex items-center gap-2.5 px-5 py-5">
    <span className="grid place-items-center h-9 w-9 rounded-[10px] bg-[var(--green)] text-[#f7fbf9]">
      <Gem className="h-5 w-5" />
    </span>
    <div className="leading-tight">
      <div className="font-serif-display text-[15px] text-[var(--text)]">Wealth Command</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Center</div>
    </div>
  </div>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-[var(--bg)]">
      {/* Desktop sidebar */}
      <aside
        data-testid="app-sidebar"
        className="hidden lg:flex flex-col w-72 shrink-0 border-r border-[color:var(--border-subtle)] bg-[var(--surface-1)] sticky top-0 h-screen"
      >
        <Brand />
        <ScrollArea className="flex-1">
          <NavList />
        </ScrollArea>
        <div className="border-t border-[color:var(--border-subtle)] p-3">
          <Button variant="outline" onClick={() => navigate("/")} data-testid="switch-center-button"
            className="w-full justify-start border-[color:var(--border-subtle)] text-[var(--text)] hover:bg-[color:rgba(198,139,60,0.10)] mb-2">
            <LayoutGrid className="h-4 w-4 mr-2 text-[var(--gold)]" /> Switch command center
          </Button>
          <div className="flex items-center gap-3 px-2 py-1.5">
            <span className="grid place-items-center h-8 w-8 rounded-full bg-[color:rgba(31,115,85,0.12)] text-[var(--green)] text-sm font-medium">
              {(user?.display_name || "U").charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-[var(--text)] truncate">{user?.display_name}</div>
              <div className="text-[11px] text-[var(--text-faint)] truncate">{user?.email}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="sign-out-button" aria-label="Sign out"
              className="h-8 w-8 text-[var(--text-muted)] hover:text-[var(--danger)]">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar (mobile + status) */}
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-[color:var(--border-subtle)] bg-[color:rgba(251,250,246,0.85)] backdrop-blur px-4 sm:px-6 h-14">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-nav-open-button" aria-label="Open navigation">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-[var(--surface-1)]" data-testid="mobile-nav-sheet">
                <Brand />
                <ScrollArea className="h-[calc(100vh-160px)]"><NavList onNavigate={() => setOpen(false)} /></ScrollArea>
                <div className="border-t border-[color:var(--border-subtle)] p-3">
                  <Button variant="ghost" onClick={logout} className="w-full justify-start text-[var(--danger)]">
                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            <span className="lg:hidden font-serif-display text-[var(--text)]">Wealth Command</span>
          </div>
          <div className="flex items-center gap-3">
            <SaveIndicator />
            <NotificationBell />
            <div className="hidden md:flex items-center gap-1.5 text-xs text-[var(--text-faint)]">
              <Command className="h-3.5 w-3.5" /> Secure session
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
